import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useServiceError } from '@/hooks';
import {
  CreatePartRequest,
  PartsQuery,
  UpdatePartRequest,
} from './Parts.types';
import {
  getPart,
  listParts,
  createPart,
  updatePart,
  deletePart,
  activeInactivePart,
  validateManufacturingCode,
  createMultiplePart,
  partValidBarCode,
} from './Parts.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { AuthorityPart } from '@/types/Authority/Authority.Part';
import { queryClient } from '@/Root';

export function useParts(params?: PartsQuery, enabled = true) {
  const cache = queryClient.getQueryData(['parts', { ...params }]);

  const query = useInfiniteQuery(
    ['parts', { ...params }],
    ({ pageParam }) => {
      const req: PartsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listParts(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: enabled && !cache,
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePart(partId: number) {
  const query = useQuery(['parts', partId], () => getPart(partId), {
    enabled: !!partId && hasPermissions([AuthorityPart.REGISTER_PART_VIEW]),
  });

  useServiceError(query.error);

  return query;
}

export function usePartMutation() {
  const mutation = useMutation((partId: number) => getPart(partId), {});

  useServiceError(mutation.error);

  return mutation;
}

export function usePartMultipleCreate() {
  return useMutation(createMultiplePart, {
    onSuccess() {
      notification.success({
        message: 'Todas as peças foram cadastradas com sucesso!',
      });
      queryClient.removeQueries(['parts']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('The part registered with this barcode already exists')
              ? 'já existe uma peça registrada com este código de barras.'
              : err,
          ),
      });
    },
  });
}

export function useCreatePart() {
  return useMutation((obj: CreatePartRequest) => createPart(obj), {
    onSuccess() {
      queryClient.removeQueries(['parts']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes(
              'The part registered with this manufacturing code already exists.',
            )
              ? 'já existe uma peça registrada com este código de fabricante.'
              : err,
          ),
      });
    },
  });
}

export function useUpdatePart() {
  const intl = useIntl();

  const mutation = useMutation((obj: UpdatePartRequest) => updatePart(obj), {
    async onSuccess() {
      await queryClient.invalidateQueries(['parts']);
      queryClient.removeQueries(['parts']);

      notification.success({
        message: intl.formatMessage({
          id: 'parts.update.notifications.success.title',
        }),
        description: intl.formatMessage({
          id: 'parts.update.notifications.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDeletePart() {
  const mutation = useMutation((id: number) => deletePart(id), {
    onSuccess() {
      queryClient.removeQueries(['parts']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Part cannot be deleted due to relation.')
              ? 'Não é possivel excluir a peça, pois ela tem vínculos com outros módulos.'
              : err.includes('Part already has a link with another entity:')
              ? 'Não é possivel excluir a peça, pois ela tem vínculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActiveInactivePart() {
  const intl = useIntl().formatMessage;
  return useMutation((id: number) => activeInactivePart(id), {
    onSuccess() {
      queryClient.removeQueries(['parts']);
      message.success(intl({ id: 'parts.status.message.success' }));
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useValidateManufacturingCode() {
  return useMutation(validateManufacturingCode, {
    onError(error) {
      notificationError(error);
    },
  });
}

export function useValidateBarCode() {
  return useMutation(partValidBarCode, {
    onError(error) {
      notificationError(error);
    },
  });
}
